/**
 * O agrupamento e o filtro da listagem de Itens.
 *
 * <p>Fora do componente porque é a única parte desta tela que se pode conferir sem
 * abrir um navegador: são duas funções puras, e é nelas que moram as decisões que
 * quebram em silêncio — a ordem dos blocos, o que fazer com a ficha sem categoria e
 * o que "achar" quer dizer numa busca escrita com pressa.
 */
import { CATEGORIAS_DE_ITEM, RARIDADES_DO_ITEM } from './esquema';
import type { Resumo } from './tipos';

/** Um bloco de subcategoria dentro de uma categoria. */
export interface BlocoDeSub {
  /** Vazio é o balde "sem subcategoria". */
  id: string;
  rotulo: string;
  itens: Resumo[];
}

/** Um bloco de categoria. `subs` vazio quer dizer que ela não se subdivide. */
export interface BlocoDeCategoria {
  /** Vazio é o balde "sem categoria". */
  id: string;
  rotulo: string;
  itens: Resumo[];
  subs: BlocoDeSub[];
}

/**
 * Tira acento e caixa para comparar.
 *
 * <p>Quem procura "pocao" tem de achar "Poção". Numa wiki escrita em português, exigir
 * o acento certo na busca é exigir que se saiba a grafia do que se está procurando —
 * e quem já sabe não precisaria procurar.
 */
export function semAcento(texto: string): string {
  // A classe do regex é o intervalo U+0300–U+036F, o bloco dos diacríticos
  // combinantes — é neles que o NFD separa o acento. Os dois extremos estão ali
  // como caractere, e caractere combinante não se enxerga no editor: se este
  // trecho parecer um colchete com um traço dentro, não está corrompido.
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * O recorte da busca e da raridade.
 *
 * <p>Os dois se somam em vez de se substituir: "poção" + "Épico" é uma pergunta que se
 * faz, e obrigar a escolher um dos dois transformaria o filtro num atalho de rolagem.
 *
 * <p><b>`raridade` tem três estados, e não dois:</b> {@code null} é "todas", uma string
 * é aquela raridade, e a string <b>vazia</b> é "sem raridade" — a pastilha que acha as
 * fichas que ninguém classificou ainda. Confundir o vazio com o nulo é o que fazia
 * "Sem raridade" devolver a lista inteira.
 *
 * <p>`regiao` segue a mesma forma, com o zero no papel da string vazia: {@code null} é
 * "todas", um id é aquela região, e {@code 0} é "sem região". Zero não é id de nada — a
 * sequência do Postgres começa em 1 —, então ele pode ser o balde sem ambiguidade.
 *
 * <p>A busca olha nome, categoria e subcategoria — os três textos que a tela mostra.
 * Procurar por "arma" e não encontrar as armas, porque a palavra está no rótulo do
 * bloco e não no nome da ficha, é o tipo de vazio que faz o time achar que o filtro
 * está quebrado.
 */
export function filtraItens(
  itens: Resumo[],
  busca: string,
  raridade: string | null,
  regiao: number | null = null,
): Resumo[] {
  const termo = semAcento(busca.trim());
  return itens.filter((i) => {
    // A região recorta pela união que o servidor já montou: marcadas à mão mais as
    // derivadas dos locais. Zero é o balde "sem região" — e, como na raridade, o
    // que distingue "todas" de "sem nenhuma" é o `null`, não um valor falsy.
    if (regiao !== null) {
      const suas = i.regioes ?? [];
      if (regiao === 0 ? suas.length > 0 : !suas.includes(regiao)) return false;
    }
    // `!== null`, e não `if (raridade)`: a pastilha "Sem raridade" escolhe a string
    // vazia, que é falsy. Com o teste de verdade, escolhê-la pulava o filtro inteiro
    // e a tela devolvia todos os itens — a resposta oposta à que foi pedida.
    //
    // Por isso também o `?? ''` do outro lado: só assim "sem raridade" na pastilha e
    // raridade ausente na ficha são a mesma coisa.
    if (raridade !== null && (i.raridade ?? '') !== raridade) return false;
    if (!termo) return true;

    const categoria = CATEGORIAS_DE_ITEM.find((c) => c.id === i.grupo);
    const sub = categoria?.sub.find((s) => s.id === i.nota);
    const alvo = semAcento(
      [i.nome, categoria?.rotulo ?? '', sub?.rotulo ?? ''].join(' '),
    );
    return alvo.includes(termo);
  });
}

/**
 * Onde uma raridade fica na fila. Três faixas, nesta ordem:
 *
 * <ol>
 *   <li>as cinco do vocabulário, na ordem em que o esquema as declara;
 *   <li>qualquer valor escrito à mão que não seja uma delas, em ordem alfabética;
 *   <li>as fichas sem raridade.
 * </ol>
 *
 * <p>A ficha sem raridade vai para o fim pelo mesmo motivo que a sem categoria: o
 * incompleto se junta no fim do bloco, onde se vê que falta preencher, em vez de se
 * misturar ao que já está pronto.
 */
function faixaDaRaridade(raridade?: string): number {
  if (!raridade) return 2;
  const posicao = RARIDADES_DO_ITEM.indexOf(raridade);
  return posicao >= 0 ? 0 : 1;
}

/**
 * A ordem dentro de um bloco: raridade primeiro, nome depois.
 *
 * <p>A raridade sobe na mesma ordem em que o filtro mostra as pastilhas e em que o
 * seletor da ficha oferece as opções — do Comum ao Lendário. Ordenar ao contrário
 * daria uma terceira ordem para o mesmo vocabulário na mesma tela.
 *
 * <p>O desempate é o nome, com {@code localeCompare} em pt-BR: `Á` tem de vir junto
 * de `A`, e a comparação crua de string põe todo acentuado depois do `Z`.
 *
 * <p>A comparação é total — raridade, depois nome — então o resultado não depende da
 * ordem em que as fichas chegaram. Um critério que empatasse deixaria a lista se
 * remexer entre dois carregamentos iguais.
 */
export function ordenaItens(itens: Resumo[]): Resumo[] {
  return [...itens].sort((a, b) => {
    const faixaA = faixaDaRaridade(a.raridade);
    const faixaB = faixaDaRaridade(b.raridade);
    if (faixaA !== faixaB) return faixaA - faixaB;

    if (faixaA === 0) {
      const posicao =
        RARIDADES_DO_ITEM.indexOf(a.raridade!) - RARIDADES_DO_ITEM.indexOf(b.raridade!);
      if (posicao !== 0) return posicao;
    } else if (faixaA === 1) {
      const entreExtras = a.raridade!.localeCompare(b.raridade!, 'pt-BR');
      if (entreExtras !== 0) return entreExtras;
    }

    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
}

/**
 * Os itens em blocos de categoria e, dentro deles, de subcategoria.
 *
 * <p>A ordem é a do esquema, e não a alfabética nem a de chegada: é a ordem em que o
 * time pensa o inventário, e a mesma que o seletor da ficha oferece. Duas ordens para
 * a mesma lista fariam a tela e o formulário discordarem.
 *
 * <p><b>Nada é escondido por não ter categoria.</b> A ficha sem categoria cai num
 * bloco no fim, e a sem subcategoria num bloco no fim da sua categoria. Some só quando
 * está vazio — esconder o incompleto é o que faz ninguém completá-lo.
 *
 * <p>`escondeVazias` existe para o filtro: com uma busca escrita, oito cabeçalhos
 * dizendo "0 itens" são oito linhas de resposta errada. Sem busca, a categoria vazia
 * fica visível de propósito, porque aí ela é o convite a preenchê-la.
 */
export function agrupaItens(
  itens: Resumo[],
  { escondeVazias = false }: { escondeVazias?: boolean } = {},
): BlocoDeCategoria[] {
  const porCategoria = new Map<string, Resumo[]>();
  itens.forEach((i) => {
    const chave = i.grupo ?? '';
    porCategoria.set(chave, [...(porCategoria.get(chave) ?? []), i]);
  });

  // Ordena uma vez por categoria, e não por bloco: os `filter` que separam as
  // subcategorias preservam a ordem relativa, então cada balde já sai ordenado — e
  // uma lista de itens não é reordenada N vezes para caber em N caixas.
  porCategoria.forEach((lista, chave) => porCategoria.set(chave, ordenaItens(lista)));

  const blocos: BlocoDeCategoria[] = CATEGORIAS_DE_ITEM.map((categoria) => {
    const daCategoria = porCategoria.get(categoria.id) ?? [];

    const subs: BlocoDeSub[] = categoria.sub.map((s) => ({
      id: s.id,
      rotulo: s.rotulo,
      itens: daCategoria.filter((i) => i.nota === s.id),
    }));

    // O balde do fim leva tudo que não caiu numa subcategoria declarada — inclusive um
    // valor escrito à mão que não exista mais no esquema, que de outro modo sumiria da
    // tela sem sumir do banco.
    if (categoria.sub.length > 0) {
      // Set<string> explícito: o esquema é `as const`, então sem a anotação o Set
      // nasceria com o tipo literal das subcategorias e recusaria comparar com o
      // texto que vem da API.
      const declaradas = new Set<string>(categoria.sub.map((s) => s.id));
      const soltos = daCategoria.filter((i) => !i.nota || !declaradas.has(i.nota));
      if (soltos.length > 0) {
        subs.push({ id: '', rotulo: 'Sem subcategoria', itens: soltos });
      }
    }

    return {
      id: categoria.id,
      rotulo: categoria.rotulo,
      itens: daCategoria,
      subs: subs.filter((s) => s.itens.length > 0),
    };
  });

  const semCategoria = porCategoria.get('') ?? [];
  if (semCategoria.length > 0) {
    blocos.push({
      id: '',
      rotulo: 'Sem categoria',
      itens: semCategoria,
      subs: [],
    });
  }

  return escondeVazias ? blocos.filter((b) => b.itens.length > 0) : blocos;
}
